import {
  extractActivityKeys,
  type Activity,
  type BoardColumn,
  type Checklist,
  type ColumnFlowRole,
  type ColumnKind,
  type GoldEntry,
  type Label,
  type ProcessTemplate,
  type Project,
  type ProjectParticipant,
  type ProjectStage,
} from '@sistemas/contracts'

import { describeDbError, getSupabase } from '@/lib/supabase'
import type { ActivityPatch } from '@/modules/projects/activity-patch'
import type { ActivityEventRow, ChatMessage, ProjectPatch } from '@/modules/projects/store'

/**
 * Persistência de C2 e C3 — projetos, quadros, atividades, eventos e chat.
 *
 * Três coisas que este módulo trata de propósito, e que o store local não tratava:
 *
 *   1. **O event log é gravado no banco, não no estado.** Ele é a base de todo
 *      indicador de tempo (lead time, previsibilidade, DORA). Evento que só
 *      existe em memória some no primeiro F5 e não volta nunca — e indicador
 *      retroativo sem histórico é impossível, não é "difícil".
 *   2. **A chave da atividade vem do banco** (`SIS-123`, via sequence). O
 *      cliente não propõe chave: ela é citada em branch, PR e chat, e um número
 *      reaproveitado corromperia tudo que já a referenciou.
 *   3. **Erro não é silencioso.** Toda escrita devolve `ok:false` com o motivo
 *      do PostgREST, para a UI conseguir dizer *o que* o banco recusou. Os
 *      triggers da migration 002 recusam coisas legítimas (responsável ausente
 *      fora do backlog, dificuldade congelada) e essas mensagens são a regra de
 *      negócio falando — não ruído para engolir.
 */

export type ProjectsResult<T> = { ok: true; data: T } | { ok: false; reason: string }

interface ProjectRow {
  id: string
  key: string
  name: string
  description: string | null
  type: Project['type']
  status: Project['status']
  visibility: Project['visibility']
  criticality: string
  leader_id: string | null
  created_by: string | null
  created_at: string
  stage_id: string
  board_position: number | string
}

interface ParticipantRow {
  project_id: string
  member_id: string
  last_at: string
}

interface StageRow {
  id: string
  name: string
  position: number | string
  status: Project['status']
}

interface ColumnRow {
  id: string
  project_id: string
  name: string
  position: number | string
  kind: ColumnKind
  wip_limit: number | string | null
  flow_role?: ColumnFlowRole | null
}

interface ActivityRow {
  id: string
  key: string
  project_id: string
  column_id: string
  position: number | string
  title: string
  description: string | null
  assignee_id: string | null
  start_on: string | null
  due_on: string | null
  completed_at: string | null
  archived_at?: string | null
  created_by?: string | null
  difficulty: number | null
  difficulty_frozen: boolean
  priority: Activity['priority']
  type: Activity['type']
  origin: Activity['origin']
  checklists: Checklist[] | null
  created_at: string
  updated_at: string
}

interface ActivityLabelRow {
  activity_id: string
  label_id: string
}

interface ActivityEventDbRow {
  id: string
  activity_id: string
  kind: string
  from_value: string | null
  to_value: string | null
  author_id?: string | null
  occurred_at: string
  /** Origem automática ("PR SISTEMAS#12"); nulo = pessoa. */
  source?: string | null
}

interface LabelRow {
  id: string
  name: string
  color: string
}

interface MessageRow {
  id: string
  project_id: string
  author_id: string
  body: string
  attachment_name: string | null
  created_at: string
}

interface ProcessRow {
  id: string
  name: string
  category: string | null
  version: number
  is_active: boolean
  items: ProcessTemplate['items'] | null
}

function dbError(error: { message?: string } | null, fallback: string): string {
  return describeDbError(error, fallback)
}

function num(value: number | string | null | undefined, fallback: number): number {
  if (value === null || value === undefined) return fallback
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : fallback
}

function toProject(row: ProjectRow): Project {
  return {
    id: row.id,
    key: row.key,
    name: row.name,
    type: row.type,
    status: row.status,
    visibility: row.visibility,
    color: row.criticality,
    createdAt: row.created_at,
    stageId: row.stage_id,
    boardPosition: num(row.board_position, 0),
    ...(row.description ? { description: row.description } : {}),
    ...(row.leader_id ? { leaderId: row.leader_id } : {}),
    ...(row.created_by ? { createdBy: row.created_by } : {}),
  }
}

function toStage(row: StageRow): ProjectStage {
  return {
    id: row.id,
    name: row.name,
    position: num(row.position, 0),
    status: row.status,
  }
}

function toColumn(row: ColumnRow): BoardColumn {
  return {
    id: row.id,
    projectId: row.project_id,
    name: row.name,
    position: num(row.position, 0),
    kind: row.kind,
    wipLimit: row.wip_limit === null ? null : num(row.wip_limit, 0),
    flowRole: row.flow_role ?? null,
  }
}

function toActivity(row: ActivityRow, labelIds: readonly string[]): Activity {
  return {
    id: row.id,
    key: row.key,
    projectId: row.project_id,
    columnId: row.column_id,
    position: num(row.position, 0),
    title: row.title,
    assigneeId: row.assignee_id,
    difficulty: row.difficulty,
    difficultyFrozen: row.difficulty_frozen,
    priority: row.priority,
    type: row.type,
    origin: row.origin,
    labelIds,
    checklists: row.checklists ?? [],
    createdAt: row.created_at,
    updatedAt: row.updated_at,
    ...(row.description ? { description: row.description } : {}),
    ...(row.start_on ? { startOn: row.start_on } : {}),
    ...(row.due_on ? { dueOn: row.due_on } : {}),
    ...(row.completed_at ? { completedAt: row.completed_at } : {}),
    ...(row.archived_at ? { archivedAt: row.archived_at } : {}),
    ...(row.created_by ? { createdBy: row.created_by } : {}),
  }
}

/**
 * `from_value`/`to_value` viram o texto único que a timeline da UI mostra.
 * O banco guarda os dois lados separados porque indicador precisa comparar
 * valores, não fazer parse de frase.
 */
function toEventRow(row: ActivityEventDbRow): ActivityEventRow {
  const detail =
    row.from_value && row.to_value
      ? `${row.from_value} → ${row.to_value}`
      : (row.to_value ?? row.from_value ?? '')

  return {
    id: row.id,
    activityId: row.activity_id,
    kind: row.kind,
    detail: row.source ? `${detail} · pelo ${row.source}` : detail,
    at: row.occurred_at,
    ...(row.author_id ? { authorId: row.author_id } : {}),
  }
}

function toMessage(row: MessageRow): ChatMessage {
  return {
    id: row.id,
    projectId: row.project_id,
    authorId: row.author_id,
    body: row.body,
    activityKeys: extractActivityKeys(row.body),
    createdAt: row.created_at,
    ...(row.attachment_name ? { attachmentName: row.attachment_name } : {}),
  }
}

function toProcess(row: ProcessRow): ProcessTemplate {
  return {
    id: row.id,
    name: row.name,
    version: row.version,
    isActive: row.is_active,
    items: row.items ?? [],
    ...(row.category ? { category: row.category } : {}),
  }
}

export interface ProjectsCatalog {
  projects: Project[]
  projectStages: ProjectStage[]
  projectParticipants: ProjectParticipant[]
  columns: BoardColumn[]
  activities: Activity[]
  activityEvents: ActivityEventRow[]
  labels: Label[]
  messages: ChatMessage[]
  processes: ProcessTemplate[]
}

/**
 * Carga inicial de C2/C3.
 *
 * Uma falha parcial derruba a carga inteira em vez de entregar meio quadro: um
 * kanban sem as atividades parece um kanban vazio, e alguém recadastraria o que
 * já existe.
 */
export async function fetchProjectsCatalog(): Promise<ProjectsResult<ProjectsCatalog>> {
  const sb = getSupabase()

  const [projectsRes, stagesRes, participantsRes, columnsRes, activitiesRes, labelLinksRes, eventsRes, labelsRes, messagesRes, processesRes] =
    await Promise.all([
      sb.from('project').select('*').order('board_position', { ascending: true }),
      sb.from('project_stage').select('*').order('position', { ascending: true }),
      // Pilha de avatares do card: quem trabalha ou trabalhou no projeto.
      sb.from('project_participant').select('*').order('last_at', { ascending: false }),
      sb.from('board_column').select('*').order('position', { ascending: true }),
      // Arquivadas ficam fora do catálogo (migration 012); `fetchArchivedActivities` lê sob demanda.
      sb.from('activity').select('*').is('archived_at', null).order('position', { ascending: true }),
      sb.from('activity_label').select('activity_id, label_id'),
      sb
        .from('activity_event')
        .select('id, activity_id, kind, from_value, to_value, author_id, occurred_at, source')
        .order('occurred_at', { ascending: false })
        .limit(2000),
      sb.from('label').select('*').order('name', { ascending: true }),
      // As 1000 mais RECENTES (desc) e inverte no cliente: com `asc` + limit, o
      // que ficava de fora era justamente o que a pessoa acabou de escrever.
      sb
        .from('project_message')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(1000),
      sb.from('process_template').select('*').eq('is_active', true).order('name'),
    ])

  const failure =
    projectsRes.error ??
    stagesRes.error ??
    participantsRes.error ??
    columnsRes.error ??
    activitiesRes.error ??
    labelLinksRes.error ??
    eventsRes.error ??
    labelsRes.error ??
    messagesRes.error ??
    processesRes.error

  if (failure) {
    return { ok: false, reason: dbError(failure, 'Falha ao carregar projetos.') }
  }

  const labelsByActivity = new Map<string, string[]>()
  for (const link of (labelLinksRes.data ?? []) as ActivityLabelRow[]) {
    const current = labelsByActivity.get(link.activity_id)
    if (current) current.push(link.label_id)
    else labelsByActivity.set(link.activity_id, [link.label_id])
  }

  return {
    ok: true,
    data: {
      projects: ((projectsRes.data ?? []) as ProjectRow[]).map(toProject),
      projectStages: ((stagesRes.data ?? []) as StageRow[]).map(toStage),
      projectParticipants: ((participantsRes.data ?? []) as ParticipantRow[]).map((r) => ({
        projectId: r.project_id,
        memberId: r.member_id,
        lastAt: r.last_at,
      })),
      columns: ((columnsRes.data ?? []) as ColumnRow[]).map(toColumn),
      activities: ((activitiesRes.data ?? []) as ActivityRow[]).map((row) =>
        toActivity(row, labelsByActivity.get(row.id) ?? []),
      ),
      activityEvents: ((eventsRes.data ?? []) as ActivityEventDbRow[]).map(toEventRow),
      labels: ((labelsRes.data ?? []) as LabelRow[]).map((r) => ({
        id: r.id,
        name: r.name,
        color: r.color,
      })),
      messages: ((messagesRes.data ?? []) as MessageRow[]).map(toMessage).reverse(),
      processes: ((processesRes.data ?? []) as ProcessRow[]).map(toProcess),
    },
  }
}

// -----------------------------------------------------------------------------
// Projeto
// -----------------------------------------------------------------------------

/** Colunas que todo quadro novo nasce com — exatamente uma `inicial` e uma `final`. */
const DEFAULT_COLUMNS: ReadonlyArray<{
  name: string
  kind: ColumnKind
  wipLimit: number | null
  /** O PR leva a atividade por estas colunas sozinho (ADR-0022, 5.6). */
  flowRole?: ColumnFlowRole
}> = [
  { name: 'Backlog', kind: 'inicial', wipLimit: null },
  { name: 'A fazer', kind: 'intermediaria', wipLimit: null },
  { name: 'Em progresso', kind: 'intermediaria', wipLimit: 3, flowRole: 'andamento' },
  { name: 'Revisão', kind: 'intermediaria', wipLimit: null, flowRole: 'revisao' },
  { name: 'Concluído', kind: 'final', wipLimit: null },
]

export async function insertProject(
  input: {
    key: string
    name: string
    description?: string | undefined
    type: Project['type']
    visibility: Project['visibility']
    color: string
    leaderId?: string | undefined
  },
  authorId: string,
): Promise<ProjectsResult<{ project: Project; columns: BoardColumn[] }>> {
  const sb = getSupabase()

  const { data, error } = await sb
    .from('project')
    .insert({
      key: input.key.toUpperCase(),
      name: input.name.trim(),
      description: input.description?.trim() || null,
      type: input.type,
      status: 'rascunho',
      visibility: input.visibility,
      criticality: input.color,
      leader_id: input.leaderId || null,
      created_by: authorId,
    })
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível criar o projeto.') }
  }

  const project = toProject(data as ProjectRow)

  const { data: columnData, error: columnError } = await sb
    .from('board_column')
    .insert(
      DEFAULT_COLUMNS.map((c, i) => ({
        project_id: project.id,
        name: c.name,
        position: (i + 1) * 1000,
        kind: c.kind,
        wip_limit: c.wipLimit,
        flow_role: c.flowRole ?? null,
      })),
    )
    .select('*')

  if (columnError || !columnData) {
    // Projeto sem quadro não recebe atividade: melhor desfazer do que deixar
    // um projeto quebrado na lista.
    await sb.from('project').delete().eq('id', project.id)
    return {
      ok: false,
      reason: dbError(columnError, 'Projeto criado sem quadro; criação desfeita.'),
    }
  }

  return {
    ok: true,
    data: {
      project,
      columns: (columnData as ColumnRow[]).map(toColumn).sort((a, b) => a.position - b.position),
    },
  }
}

export async function patchProject(
  id: string,
  patch: ProjectPatch,
): Promise<ProjectsResult<Project>> {
  const sb = getSupabase()
  const payload: Record<string, unknown> = {}

  if (patch.key !== undefined) payload.key = patch.key?.toUpperCase()
  if (patch.name !== undefined) payload.name = patch.name?.trim()
  if (patch.description !== undefined) payload.description = patch.description?.trim() || null
  if (patch.type !== undefined) payload.type = patch.type
  if (patch.status !== undefined) payload.status = patch.status
  if (patch.visibility !== undefined) payload.visibility = patch.visibility
  if (patch.color !== undefined) payload.criticality = patch.color
  if (patch.leaderId !== undefined) payload.leader_id = patch.leaderId || null

  if (Object.keys(payload).length === 0) {
    return { ok: false, reason: 'Nada para salvar.' }
  }

  const { data, error } = await sb
    .from('project')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível salvar o projeto.') }
  }
  return { ok: true, data: toProject(data as ProjectRow) }
}

/**
 * Arrastar no quadro de Projetos: coluna e posição numa escrita só. A fase
 * (`status`) não vai no payload — o trigger `project_sync_stage` a deriva da
 * coluna, e mandar as duas abriria espaço para divergirem.
 */
export async function moveProjectRemote(
  id: string,
  stageId: string,
  boardPosition: number,
): Promise<ProjectsResult<Project>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('project')
    .update({ stage_id: stageId, board_position: boardPosition })
    .eq('id', id)
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível mover o projeto.') }
  }
  return { ok: true, data: toProject(data as ProjectRow) }
}

export async function deleteProjectRemote(id: string): Promise<ProjectsResult<true>> {
  const sb = getSupabase()
  const { error } = await sb.from('project').delete().eq('id', id)
  if (error) {
    return { ok: false, reason: dbError(error, 'Não foi possível excluir o projeto.') }
  }
  return { ok: true, data: true }
}

// -----------------------------------------------------------------------------
// Colunas do quadro de atividades de um projeto
// -----------------------------------------------------------------------------

export async function insertColumn(input: {
  projectId: string
  name: string
  position: number
  wipLimit: number | null
}): Promise<ProjectsResult<BoardColumn>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('board_column')
    .insert({
      project_id: input.projectId,
      name: input.name.trim(),
      position: input.position,
      kind: 'intermediaria',
      wip_limit: input.wipLimit,
    })
    .select('*')
    .single()
  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível criar a coluna.') }
  }
  return { ok: true, data: toColumn(data as ColumnRow) }
}

export async function patchColumn(
  id: string,
  patch: { name?: string; position?: number; wipLimit?: number | null; flowRole?: ColumnFlowRole | null },
): Promise<ProjectsResult<BoardColumn>> {
  const sb = getSupabase()
  // Papel no fluxo do PR é um por quadro: tirar de quem tinha antes de dar a
  // esta (o índice único recusaria a troca direta).
  if (patch.flowRole) {
    const { data: current } = await sb.from('board_column').select('project_id').eq('id', id).single()
    if (current) {
      await sb
        .from('board_column')
        .update({ flow_role: null })
        .eq('project_id', (current as { project_id: string }).project_id)
        .eq('flow_role', patch.flowRole)
        .neq('id', id)
    }
  }
  const payload: Record<string, unknown> = {}
  if (patch.name !== undefined) payload.name = patch.name.trim()
  if (patch.position !== undefined) payload.position = patch.position
  if (patch.wipLimit !== undefined) payload.wip_limit = patch.wipLimit
  if (patch.flowRole !== undefined) payload.flow_role = patch.flowRole
  const { data, error } = await sb
    .from('board_column')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single()
  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível salvar a coluna.') }
  }
  return { ok: true, data: toColumn(data as ColumnRow) }
}

/** Exclui a coluna levando as atividades para o fim de `targetId` (RPC, uma transação). */
export async function deleteColumnRemote(
  id: string,
  targetId: string | null,
): Promise<ProjectsResult<true>> {
  const sb = getSupabase()
  const { error } = await sb.rpc('delete_board_column', {
    p_column_id: id,
    p_target_column_id: targetId,
  })
  if (error) {
    return { ok: false, reason: dbError(error, 'Não foi possível excluir a coluna.') }
  }
  return { ok: true, data: true }
}

// -----------------------------------------------------------------------------
// Colunas do quadro de Projetos (etapas)
// -----------------------------------------------------------------------------

export async function insertStage(
  input: { name: string; status: Project['status']; position: number },
  authorId: string,
): Promise<ProjectsResult<ProjectStage>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('project_stage')
    .insert({
      name: input.name.trim(),
      status: input.status,
      position: input.position,
      created_by: authorId,
    })
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: stageError(error, 'Não foi possível criar a coluna.') }
  }
  return { ok: true, data: toStage(data as StageRow) }
}

export async function patchStage(
  id: string,
  patch: { name?: string; status?: Project['status']; position?: number },
): Promise<ProjectsResult<ProjectStage>> {
  const sb = getSupabase()
  const payload: Record<string, unknown> = {}
  if (patch.name !== undefined) payload.name = patch.name.trim()
  if (patch.status !== undefined) payload.status = patch.status
  if (patch.position !== undefined) payload.position = patch.position

  const { data, error } = await sb
    .from('project_stage')
    .update(payload)
    .eq('id', id)
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: stageError(error, 'Não foi possível salvar a coluna.') }
  }
  return { ok: true, data: toStage(data as StageRow) }
}

/** Exclui a coluna levando os projetos dela para o fim de `targetId` (RPC, uma transação). */
export async function deleteStageRemote(
  id: string,
  targetId: string | null,
): Promise<ProjectsResult<true>> {
  const sb = getSupabase()
  const { error } = await sb.rpc('delete_project_stage', {
    p_stage_id: id,
    p_target_stage_id: targetId,
  })
  if (error) {
    return { ok: false, reason: stageError(error, 'Não foi possível excluir a coluna.') }
  }
  return { ok: true, data: true }
}

/** O índice único de nome volta como `duplicate key`; a pessoa precisa ler o motivo. */
function stageError(error: { message?: string; code?: string } | null, fallback: string): string {
  if (error?.code === '23505') return 'Já existe uma coluna com esse nome.'
  return dbError(error, fallback)
}

// -----------------------------------------------------------------------------
// Atividade
// -----------------------------------------------------------------------------

export async function insertActivity(
  input: {
    projectId: string
    columnId: string
    position: number
    title: string
    description?: string | undefined
    assigneeId: string | null
    startOn?: string | undefined
    dueOn?: string | undefined
    difficulty: number | null
    priority: Activity['priority']
    type?: Activity['type'] | undefined
    origin: Activity['origin']
    labelIds: readonly string[]
    checklists?: readonly Checklist[] | undefined
  },
  _authorId: string,
): Promise<ProjectsResult<{ activity: Activity; event: ActivityEventRow }>> {
  const sb = getSupabase()

  // Uma transação no banco (migration 011): atividade, etiquetas e o evento
  // "criada" nascem juntos ou não nascem. Antes eram três escritas em série e
  // a etiqueta que falhava deixava a atividade órfã — o segundo clique
  // duplicava. `key` e `created_by` são decididos pelo banco.
  const { data, error } = await sb.rpc('create_activity', {
    p_project_id: input.projectId,
    p_column_id: input.columnId,
    p_position: input.position,
    p_title: input.title,
    p_description: input.description?.trim() || null,
    p_assignee_id: input.assigneeId,
    p_priority: input.priority,
    p_difficulty: input.difficulty,
    p_start_on: input.startOn || null,
    p_due_on: input.dueOn || null,
    p_origin: input.origin,
    p_type: input.type ?? 'tarefa',
    p_label_ids: [...input.labelIds],
    p_checklists: input.checklists ?? [],
  })

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível criar a atividade.') }
  }

  const out = data as { activity: ActivityRow; event: ActivityEventDbRow }
  return {
    ok: true,
    data: { activity: toActivity(out.activity, input.labelIds), event: toEventRow(out.event) },
  }
}

/**
 * Resultado de uma RPC que devolve `{ activity, event }` (create_activity e
 * quem a chama por dentro, como `triage_ticket`) já no formato do store.
 */
export function activityFromRpc(
  data: unknown,
  labelIds: readonly string[] = [],
): { activity: Activity; event: ActivityEventRow } {
  const out = data as { activity: ActivityRow; event: ActivityEventDbRow }
  return { activity: toActivity(out.activity, labelIds), event: toEventRow(out.event) }
}

/**
 * Move ou reordena a atividade. O banco decide `completed_at` (só nas
 * transições com a coluna final) e grava o evento na mesma transação — e só
 * quando a coluna muda. Devolve o evento para a timeline, ou `null` quando
 * foi apenas reordenação.
 */
export async function moveActivityRemote(
  input: { activityId: string; columnId: string; position: number; labelIds: readonly string[] },
): Promise<ProjectsResult<{ activity: Activity; event: ActivityEventRow | null }>> {
  const sb = getSupabase()
  const { data, error } = await sb.rpc('move_activity', {
    p_activity_id: input.activityId,
    p_column_id: input.columnId,
    p_position: input.position,
  })

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível mover a atividade.') }
  }

  const out = data as { activity: ActivityRow; event: ActivityEventDbRow | null }
  return {
    ok: true,
    data: {
      activity: toActivity(out.activity, input.labelIds),
      event: out.event ? toEventRow(out.event) : null,
    },
  }
}

export async function patchActivity(
  id: string,
  patch: ActivityPatch,
): Promise<ProjectsResult<Activity>> {
  const sb = getSupabase()
  const payload: Record<string, unknown> = {}

  // Presença da chave decide; `null` limpa. `undefined` nunca chega aqui
  // (ver ActivityPatch) — era isso que impedia tirar um prazo.
  if ('title' in patch && patch.title !== undefined) payload.title = patch.title.trim()
  if ('description' in patch) payload.description = patch.description?.trim() || null
  if ('assigneeId' in patch) payload.assignee_id = patch.assigneeId ?? null
  if ('startOn' in patch) payload.start_on = patch.startOn || null
  if ('dueOn' in patch) payload.due_on = patch.dueOn || null
  if ('difficulty' in patch) payload.difficulty = patch.difficulty ?? null
  if ('priority' in patch) payload.priority = patch.priority
  if ('type' in patch) payload.type = patch.type
  if ('checklists' in patch) payload.checklists = patch.checklists
  if ('columnId' in patch) payload.column_id = patch.columnId
  if ('position' in patch) payload.position = patch.position
  if ('completedAt' in patch) payload.completed_at = patch.completedAt ?? null

  const labelIds = patch.labelIds

  if (Object.keys(payload).length === 0 && labelIds === undefined) {
    return { ok: false, reason: 'Nada para salvar.' }
  }

  let row: ActivityRow | null = null
  if (Object.keys(payload).length > 0) {
    const { data, error } = await sb
      .from('activity')
      .update(payload)
      .eq('id', id)
      .select('*')
      .single()
    if (error || !data) {
      return { ok: false, reason: dbError(error, 'Não foi possível salvar a atividade.') }
    }
    row = data as ActivityRow
  }

  if (labelIds !== undefined) {
    const replaced = await replaceActivityLabels(id, labelIds)
    if (!replaced.ok) return { ok: false, reason: replaced.reason }
  }

  if (!row) {
    // Só etiquetas mudaram: reler a linha para devolver o mesmo formato.
    const { data, error } = await sb.from('activity').select('*').eq('id', id).single()
    if (error || !data) {
      return { ok: false, reason: dbError(error, 'Não foi possível reler a atividade.') }
    }
    row = data as ActivityRow
  }

  return {
    ok: true,
    data: toActivity(row, labelIds ?? (await currentLabelIds(id))),
  }
}

async function currentLabelIds(activityId: string): Promise<string[]> {
  const sb = getSupabase()
  const { data } = await sb.from('activity_label').select('label_id').eq('activity_id', activityId)
  return ((data ?? []) as Array<{ label_id: string }>).map((r) => r.label_id)
}

async function replaceActivityLabels(
  activityId: string,
  labelIds: readonly string[],
): Promise<ProjectsResult<true>> {
  const sb = getSupabase()
  const { error: deleteError } = await sb
    .from('activity_label')
    .delete()
    .eq('activity_id', activityId)
  if (deleteError) {
    return { ok: false, reason: dbError(deleteError, 'Falha ao atualizar etiquetas.') }
  }
  if (labelIds.length === 0) return { ok: true, data: true }

  const { error } = await sb
    .from('activity_label')
    .insert(labelIds.map((labelId) => ({ activity_id: activityId, label_id: labelId })))
  if (error) {
    return { ok: false, reason: dbError(error, 'Falha ao gravar etiquetas.') }
  }
  return { ok: true, data: true }
}

/**
 * Arquiva (soft-delete, migration 012). O banco grava o evento "arquivada" na
 * mesma transação e recusa atividade de chamado em aberto (R11).
 */
export async function archiveActivityRemote(
  id: string,
  labelIds: readonly string[],
): Promise<ProjectsResult<{ activity: Activity; event: ActivityEventRow | null }>> {
  return callArchiveRpc('archive_activity', id, labelIds, 'Não foi possível arquivar a atividade.')
}

export async function restoreActivityRemote(
  id: string,
  labelIds: readonly string[],
): Promise<ProjectsResult<{ activity: Activity; event: ActivityEventRow | null }>> {
  return callArchiveRpc('restore_activity', id, labelIds, 'Não foi possível restaurar a atividade.')
}

async function callArchiveRpc(
  fn: 'archive_activity' | 'restore_activity' | 'trash_activity' | 'restore_activity_from_trash',
  id: string,
  labelIds: readonly string[],
  fallback: string,
): Promise<ProjectsResult<{ activity: Activity; event: ActivityEventRow | null }>> {
  const sb = getSupabase()
  const { data, error } = await sb.rpc(fn, { p_activity_id: id })
  if (error || !data) return { ok: false, reason: dbError(error, fallback) }
  const out = data as { activity: ActivityRow; event: ActivityEventDbRow | null }
  return {
    ok: true,
    data: {
      activity: toActivity(out.activity, labelIds),
      event: out.event ? toEventRow(out.event) : null,
    },
  }
}

/**
 * Manda para a Lixeira (ADR-0017). O banco grava o evento "excluida" na mesma
 * transação, recusa chamado em aberto (R11) e confere quem pode.
 */
export async function trashActivityRemote(
  id: string,
  labelIds: readonly string[],
): Promise<ProjectsResult<{ activity: Activity; event: ActivityEventRow | null }>> {
  return callArchiveRpc('trash_activity', id, labelIds, 'Não foi possível excluir a atividade.')
}

export async function restoreActivityFromTrashRemote(
  id: string,
): Promise<ProjectsResult<{ activity: Activity; event: ActivityEventRow | null }>> {
  return callArchiveRpc('restore_activity_from_trash', id, [], 'Não foi possível restaurar a atividade.')
}

/** Um item da Lixeira do projeto, como `list_activity_trash` devolve. */
export interface TrashItem {
  readonly id: string
  readonly key: string
  readonly title: string
  readonly columnName: string
  readonly assigneeId: string | null
  readonly deletedAt: string
  readonly deletedBy: string | null
  readonly expiresAt: string
  readonly canRestore: boolean
}

/** A Lixeira do projeto: excluídas nos últimos 30 dias, mais recentes primeiro. */
export async function fetchActivityTrash(projectId: string): Promise<ProjectsResult<TrashItem[]>> {
  const { data, error } = await getSupabase().rpc('list_activity_trash', { p_project_id: projectId })
  if (error) return { ok: false, reason: dbError(error, 'Não foi possível ler a Lixeira.') }
  const rows = (data ?? []) as {
    id: string; key: string; title: string; column_name: string; assignee_id: string | null
    deleted_at: string; deleted_by: string | null; expires_at: string; can_restore: boolean
  }[]
  return {
    ok: true,
    data: rows.map((r) => ({
      id: r.id,
      key: r.key,
      title: r.title,
      columnName: r.column_name,
      assigneeId: r.assignee_id,
      deletedAt: r.deleted_at,
      deletedBy: r.deleted_by,
      expiresAt: r.expires_at,
      canRestore: r.can_restore,
    })),
  }
}

/** Arquivadas de um projeto, mais recentes primeiro. Etiquetas não são carregadas aqui. */
export async function fetchArchivedActivities(projectId: string): Promise<ProjectsResult<Activity[]>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('activity')
    .select('*')
    .eq('project_id', projectId)
    .not('archived_at', 'is', null)
    .order('archived_at', { ascending: false })
  if (error) return { ok: false, reason: dbError(error, 'Não foi possível ler as arquivadas.') }
  return { ok: true, data: ((data ?? []) as ActivityRow[]).map((row) => toActivity(row, [])) }
}

// -----------------------------------------------------------------------------
// Event log (I4 — imutável)
// -----------------------------------------------------------------------------

export type { ActivityEventKind } from '@sistemas/contracts'
import type { ActivityEventKind } from '@sistemas/contracts'

export async function insertActivityEvent(
  input: {
    activityId: string
    kind: ActivityEventKind
    from?: string | undefined
    to?: string | undefined
  },
  authorId: string,
): Promise<ProjectsResult<ActivityEventRow>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('activity_event')
    .insert({
      activity_id: input.activityId,
      kind: input.kind,
      from_value: input.from ?? null,
      to_value: input.to ?? null,
      author_id: authorId,
    })
    .select('id, activity_id, kind, from_value, to_value, author_id, occurred_at')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Falha ao registrar o evento da atividade.') }
  }
  return { ok: true, data: toEventRow(data as ActivityEventDbRow) }
}

// -----------------------------------------------------------------------------
// Chat do projeto (C3)
// -----------------------------------------------------------------------------

/**
 * As mensagens mais recentes de um projeto — o chat se atualiza com isto a
 * cada 15 s enquanto está aberto (sem realtime ainda).
 */
export async function fetchProjectMessages(
  projectId: string,
  limit = 300,
): Promise<ProjectsResult<ChatMessage[]>> {
  const { data, error } = await getSupabase()
    .from('project_message')
    .select('*')
    .eq('project_id', projectId)
    .order('created_at', { ascending: false })
    .limit(limit)
  if (error) return { ok: false, reason: dbError(error, 'Não foi possível atualizar o chat.') }
  return { ok: true, data: ((data ?? []) as MessageRow[]).map(toMessage).reverse() }
}

export async function insertProjectMessage(
  input: { projectId: string; body: string; attachmentName?: string | undefined },
  authorId: string,
): Promise<ProjectsResult<ChatMessage>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('project_message')
    .insert({
      project_id: input.projectId,
      author_id: authorId,
      body: input.body,
      attachment_name: input.attachmentName || null,
    })
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível enviar a mensagem.') }
  }
  return { ok: true, data: toMessage(data as MessageRow) }
}

/**
 * Vínculo projeto ↔ repositório do GitHub (D22).
 *
 * É obrigatório para o projeto sair de rascunho, e a tag que aparece na UI é o
 * nome do repositório — então perder este vínculo é perder a identidade visual
 * do projeto, não só um campo.
 */
export async function linkRepository(
  projectId: string,
  repositoryId: string | null,
): Promise<ProjectsResult<true>> {
  const sb = getSupabase()

  // Um projeto tem um repositório: trocar é remover o anterior.
  const { error: clearError } = await sb
    .from('project_repository')
    .delete()
    .eq('project_id', projectId)
  if (clearError) {
    return { ok: false, reason: dbError(clearError, 'Falha ao desvincular o repositório.') }
  }

  if (!repositoryId) return { ok: true, data: true }

  const { error } = await sb
    .from('project_repository')
    .insert({ project_id: projectId, repository_id: repositoryId })
  if (error) {
    return { ok: false, reason: dbError(error, 'Falha ao vincular o repositório.') }
  }
  return { ok: true, data: true }
}

/**
 * Reatribuição em lote — usada no offboarding, antes de desativar alguém.
 *
 * O evento por atividade é obrigatório: sem ele, o histórico mostraria o
 * trabalho mudando de dono sozinho, e a métrica por pessoa ficaria sem
 * explicação para o salto.
 */
export async function reassignActivitiesRemote(
  activityIds: readonly string[],
  toMemberId: string,
  authorId: string,
): Promise<ProjectsResult<Activity[]>> {
  if (activityIds.length === 0) return { ok: true, data: [] }

  const sb = getSupabase()
  const { data, error } = await sb
    .from('activity')
    .update({ assignee_id: toMemberId })
    .in('id', activityIds)
    .select('*')

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Não foi possível reatribuir as atividades.') }
  }

  for (const id of activityIds) {
    const event = await insertActivityEvent(
      { activityId: id, kind: 'responsavel_alterado', to: toMemberId },
      authorId,
    )
    if (!event.ok) return { ok: false, reason: event.reason }
  }

  const rows = data as ActivityRow[]
  return { ok: true, data: rows.map((row) => toActivity(row, [])) }
}

/** Crédito e estorno de GOLD (C9). Saldo é soma de transações, nunca coluna. */
export async function insertGoldEntry(
  input: {
    memberId: string
    points: number
    reason: 'atividade_concluida' | 'ajuste_manual' | 'bonus' | 'estorno'
    activityKey?: string | undefined
    description: string
    month: string
  },
  authorId: string,
): Promise<ProjectsResult<GoldEntry>> {
  const sb = getSupabase()
  const { data, error } = await sb
    .from('gold_entry')
    .insert({
      member_id: input.memberId,
      points: input.points,
      reason: input.reason,
      activity_key: input.activityKey ?? null,
      description: input.description,
      month: input.month,
      created_by: authorId,
    })
    .select('*')
    .single()

  if (error || !data) {
    return { ok: false, reason: dbError(error, 'Falha ao registrar o GOLD.') }
  }
  const row = data as {
    id: string; member_id: string; points: number; reason: GoldEntry['reason']
    activity_key: string | null; description: string; month: string; created_at: string
  }
  return {
    ok: true,
    data: {
      id: row.id,
      memberId: row.member_id,
      points: row.points,
      reason: row.reason,
      description: row.description,
      month: row.month,
      createdAt: row.created_at,
      ...(row.activity_key ? { activityKey: row.activity_key } : {}),
    },
  }
}
